import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import { CircularProgress } from '@mui/material'
import { FileWithPath } from 'react-dropzone'
import { Body1, H5Bold, H6Bold } from '../../components/Typography'
import { Box } from '../../components/Box'
import { Flex } from '../../components/Flex'
import { ButtonGreen } from '../../components/Buttons'
import { colors, spacing } from '../../theme'
import { SnackAlert } from '../../components/SnackAlert'
import { Dropzone } from '../../components/Dropzone'
import { UploadInfo, useFileUploadState } from '../../hooks/useFileUploadState'
import { captureException } from '../../utils/Sentry'
import {
  FileType,
  useValidateAndRegisterFileMutation,
} from '../../graphql/generated'

export function ExcelFileUpload(): React.JSX.Element {
  const {
    files,
    uploadProgress,
    addFilesToMap,
    removeFileFromMap,
    setUploadProgress,
    removeFiles,
    computeOverallProgress,
    uploadFile,
  } = useFileUploadState()

  const [error, setError] = React.useState('')
  const [success, setSuccess] = React.useState(false)
  const [validateAndRegisterFile] = useValidateAndRegisterFileMutation({
    fetchPolicy: 'no-cache',
  })

  type FormValues = {
    files?: FileWithPath[]
  }

  const initialValues: FormValues = {
    files: [],
  }

  const { setFieldValue, handleSubmit } = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setError('')
      // Initialize progress for all files to be uploaded
      const initialProgress: UploadInfo[] = []
      if (values.files) {
        values.files.forEach((file) => {
          initialProgress.push({ loaded: 0, total: file.size })
        })
      }
      setUploadProgress(initialProgress)

      try {
        if (values.files && values.files.length > 0) {
          const uploadPromises = values.files.map(uploadFile)
          const fileInfos = await Promise.all(uploadPromises)

          const successFiles: FileWithPath[] = []

          const checkPromises = fileInfos.map(async (fileInfo) => {
            const checkResult = await validateAndRegisterFile({
              variables: {
                fileId: fileInfo.id,
                fileType: FileType.Other,
                fileName: fileInfo.file.name,
              },
            })
            if (checkResult.data?.validateAndRegisterFile.error) {
              setError('Något gick fel vid uppladdningen, försök igen')
            } else {
              successFiles.push(fileInfo.file)
            }
          })
          await Promise.all(checkPromises)
          if (successFiles.length > 0) {
            removeFiles(successFiles)
          }
          if (successFiles.length === values.files.length) {
            setSuccess(true)
          }
        }
      } catch (err) {
        captureException(err)
        setError('Något gick fel vid uppladdningen, försök igen')
      }
      setUploadProgress([])
    },
  })

  useEffect(() => {
    setFieldValue('files', Array.from(files.values()))
  }, [setFieldValue, files])

  return (
    <>
      <SnackAlert open={!!error} severity='error' onClose={() => setError('')}>
        <Body1>{error}</Body1>
      </SnackAlert>
      <SnackAlert
        open={success}
        severity='success'
        onClose={() => setSuccess(false)}
      >
        <Body1>Filerna har laddats upp.</Body1>
      </SnackAlert>
      <Box>
        <Flex
          column
          stretchWidth
          style={{ padding: spacing.large, gap: spacing.large }}
        >
          <H5Bold>Ladda upp filer</H5Bold>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Flex column style={{ gap: spacing.medium }}>
              <H6Bold>
                <span>Filer&nbsp;</span>
              </H6Bold>
              <Dropzone
                text='Dra och släpp filer här, eller klicka för att välja filer'
                files={[...files.values()]}
                addFiles={addFilesToMap}
                removeFile={removeFileFromMap}
                multiple
              />
              <ButtonGreen
                type='submit'
                endIcon={
                  uploadProgress.length > 0 && (
                    <CircularProgress
                      variant='determinate'
                      value={computeOverallProgress()}
                      style={{ color: colors.white }}
                      size={20}
                    />
                  )
                }
                disabled={uploadProgress.length > 0 || !files.size}
              >
                Ladda upp
              </ButtonGreen>
            </Flex>
          </form>
        </Flex>
      </Box>
    </>
  )
}
